import { useState } from "react";
import { toast } from "sonner";
import { Megaphone, Eye, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";

export default function AdminBroadcast() {
  const [broadcast, setBroadcast] = useState({ title: "", body: "", target: "all" });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const valid = broadcast.title.trim() && broadcast.body.trim();
  const audienceLabel = { all: "All users", hosts: "Hosts", drivers: "Drivers" }[broadcast.target];

  const send = async () => {
    setBusy(true); // duplicate-submission guard: button stays disabled in flight
    try {
      const r = await api.adminBroadcast(broadcast);
      toast.success(`Notification sent to ${r.sent} user(s)`);
      setConfirming(false);
      setBroadcast({ title: "", body: "", target: "all" });
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-broadcast-page">
      <div className="card-dark p-7">
        <div className="flex items-center gap-2.5">
          <Megaphone className="w-4 h-4 text-[#FF3B45]" />
          <h2 className="font-display font-semibold text-lg">Notification composer</h2>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label htmlFor="bc-target" className="label-dark">Audience</label>
            <select id="bc-target" data-testid="broadcast-target" value={broadcast.target} onChange={(e) => setBroadcast({ ...broadcast, target: e.target.value })} className="input-dark">
              {[["all", "All users"], ["hosts", "Hosts"], ["drivers", "Drivers"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="bc-title" className="label-dark">Title</label>
            <input id="bc-title" data-testid="broadcast-title" value={broadcast.title} onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })} className="input-dark" />
          </div>
          <div>
            <label htmlFor="bc-body" className="label-dark">Message</label>
            <textarea id="bc-body" data-testid="broadcast-body" rows={4} value={broadcast.body} onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })} className="input-dark !h-auto py-3" />
          </div>
        </div>
      </div>

      {/* Preview — exactly what the recipient sees */}
      <div className="card-dark p-7">
        <div className="flex items-center gap-2.5">
          <Eye className="w-4 h-4 text-[#FF3B45]" />
          <h3 className="font-display font-semibold text-base">Recipient preview</h3>
        </div>
        <div className="mt-4 rounded-xl border border-[#27272A] bg-[#0D1114]/70 p-4" data-testid="broadcast-preview">
          <p className="text-[10px] uppercase tracking-wider text-zinc-600">To: {audienceLabel}</p>
          <p className="mt-2 text-sm font-semibold text-white" data-testid="preview-title">{broadcast.title.trim() || "Notification title"}</p>
          <p className="mt-1 text-sm text-zinc-400 leading-relaxed whitespace-pre-wrap" data-testid="preview-body">{broadcast.body.trim() || "Your message appears here…"}</p>
        </div>
        <button data-testid="broadcast-send" disabled={!valid || busy} onClick={() => setConfirming(true)}
                className="btn-primary mt-5 text-sm disabled:opacity-40">Send notification</button>
      </div>

      {confirming && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5" data-testid="broadcast-confirm" role="alertdialog" aria-label="Confirm notification">
          <div className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="w-4 h-4" />
            <p className="text-sm font-semibold">Send notification to {audienceLabel.toLowerCase()}?</p>
          </div>
          <p className="mt-2 text-xs text-zinc-400">“{broadcast.title.trim()}” will be delivered to every {audienceLabel.toLowerCase().replace(/s$/, "")} account's notification inbox immediately. This cannot be unsent.</p>
          <div className="mt-4 flex gap-2">
            <button data-testid="broadcast-confirm-send" disabled={busy} onClick={send} className="btn-primary !py-2 text-xs disabled:opacity-40">{busy ? "Sending…" : "Confirm & send"}</button>
            <button data-testid="broadcast-cancel" disabled={busy} onClick={() => setConfirming(false)} className="btn-secondary !py-2 text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
